import { useMemo } from 'react'
import { Eraser, Pause, Play, Plane, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useEventPlannerStore } from '../store/eventPlannerStore'
import { SLOT_MS, PLAYBACK_RATES, rateToSpeed } from '../data/eventWindow'
import { formatDateTime } from '../data/formatTime'

export function TimelineBar() {
  const windowStart = useEventPlannerStore((s) => s.windowStart)
  const windowEnd = useEventPlannerStore((s) => s.windowEnd)
  const currentTime = useEventPlannerStore((s) => s.currentTime)
  const playback = useEventPlannerStore((s) => s.playback)
  const speed = useEventPlannerStore((s) => s.speed)
  const placements = useEventPlannerStore((s) => s.placements)

  const play = useEventPlannerStore((s) => s.play)
  const pause = useEventPlannerStore((s) => s.pause)
  const seek = useEventPlannerStore((s) => s.seek)
  const setSpeed = useEventPlannerStore((s) => s.setSpeed)
  const resetStage = useEventPlannerStore((s) => s.resetStage)

  const onField = useMemo(
    () =>
      Object.values(placements).filter(
        (p) => p.arrival <= currentTime && currentTime < p.departure
      ).length,
    [placements, currentTime]
  )

  const isPlaying = playback === 'playing'
  const atEnd = currentTime >= windowEnd

  const replay = () => {
    seek(windowStart)
    play()
  }

  return (
    <div className="pointer-events-auto absolute bottom-6 left-1/2 w-[760px] max-w-[calc(100vw-420px)] -translate-x-1/2 rounded-xl border border-white/10 bg-slate-950/85 px-4 py-3 backdrop-blur-md">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => (atEnd ? replay() : isPlaying ? pause() : play())}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-sky-500/30 text-sky-100 hover:bg-sky-500/40"
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={replay}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-white/10 text-white/70 hover:bg-white/20"
            title="Replay from start"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={resetStage}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-white/10 text-white/70 hover:bg-white/20"
            title="Reset stage — rebuild every 3D model at the current time (clears any leftovers)"
          >
            <Eraser className="h-4 w-4" />
          </button>
          <span className="ml-1 whitespace-nowrap text-sm font-semibold text-white">
            {formatDateTime(currentTime)}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-xs text-white/60">
            <Plane className="h-3.5 w-3.5" /> {onField} on field
          </span>
          <div
            className="flex overflow-hidden rounded-md border border-white/10"
            title="Playback rate — sim time advanced per real second"
          >
            {PLAYBACK_RATES.map((rate) => {
              const mult = rateToSpeed(rate.simSeconds)
              const active = Math.abs(speed - mult) < mult * 0.05
              return (
                <button
                  key={rate.label}
                  type="button"
                  onClick={() => setSpeed(mult)}
                  className={cn(
                    'px-1.5 py-1 text-[11px] font-medium tabular-nums',
                    active ? 'bg-sky-500/30 text-sky-200' : 'text-white/50 hover:bg-white/5'
                  )}
                >
                  {rate.label}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <input
        type="range"
        min={windowStart}
        max={windowEnd}
        step={SLOT_MS}
        value={currentTime}
        onChange={(e) => seek(Number(e.target.value))}
        className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/15 accent-sky-400"
      />
    </div>
  )
}
